import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface TutorialStep {
  id: string;
  title: string;
  description: string;
  mobileDescription?: string; // Mobile-specific description
  targetElement?: string; // CSS selector or element identifier
  mobileTargetElement?: string; // Mobile-specific target element
  position?: 'top' | 'bottom' | 'left' | 'right';
  mobilePosition?: 'top' | 'bottom' | 'left' | 'right'; // Mobile-specific position
}

export interface TutorialState {
  hasSeenTutorial: boolean;
  isActive: boolean;
  currentStepIndex: number;
  showWelcomePopup: boolean;
  steps: TutorialStep[];
}

const tutorialSteps: TutorialStep[] = [
  {
    id: 'model-selector',
    title: '🤖 AI Model Selector (Reasoning Mode, Image Generation)',
    description: 'Choose your AI model from this dropdown.\n\nNote: Reasoning and Image Generation tools can now be found here. Enable them by selecting a reasoning or image generation model (e.g. "Claude Opus 4.7", "GPT-Image-1").',
    // mobileDescription: 'The AI Model Selector lets you choose between fast models and reasoning-enabled models. Selecting a reasoning model (e.g. "GPT 5.4") activates Reasoning Mode — previously a separate tool button.',
    targetElement: 'model-selector',
    position: 'bottom'
  },
  {
    id: 'file-upload',
    title: '📁 File Upload Enhancements',
    description: 'More flexibility with multiple text/data files.\n\nTip: Drag & drop works here too. Maximum sizes increased to 5MB for images.',
    targetElement: 'byod-button',
    position: 'right'
  },
  {
    id: 'web-search',
    title: '🌐 Web Search Tool',
    description: "Search the web directly from the app to enrich 3M Navigator's responses.\n\nTip: Use keywords + context for best results.",
    targetElement: 'web-search-tool',
    position: 'right'
  },
  {
    id: 'data-analysis',
    title: '📊 Data Analysis Tool',
    description: 'Analyze Excel spreadsheets and CSVs on the fly.\n\nTip: You can now upload 2 data files at once. Combine them for richer analysis.',
    targetElement: 'data-analysis-tool',
    position: 'right'
  },
  {
    id: 'document-translation',
    title: '🌐 Document Translation Tool',
    description: 'Translate entire documents between languages while preserving formatting. Upload up to 5 documents at a time and translate to up to 2 languages at once.\n\nTip: Upload Word docs, PDFs, or text files and select your target language(s).',
    targetElement: 'document-translation-tool',
    position: 'right'
  },
  {
    id: 'voice-mode',
    title: '🎙️ Voice Mode',
    description: 'Have natural voice conversations with 3M Navigator.\n\nTip: Click the microphone icon to start and Navigator will ask for permission to use your microphone.',
    targetElement: 'voice-mode-button',
    position: 'left'
  },
  {
    id: 'knowledge-tab',
    title: '🏛️ 3M Knowledge',
    description: 'Beyond the standard chat, 3M Knowledge gives you access to specialized AI assistants.\n\nChoose from different chats such as Legal, DevOps, TEBG, Travel & Expenses, HR, Reporting Portal, and Everyday Insights to get answers grounded in verified 3M sources rather than the open web.',
    targetElement: 'knowledge-tab',
    position: 'bottom'
  },
  {
    id: 'stats-button',
    title: '📊 Statistics',
    description: 'View usage statistics and analytics for 3M Navigator.',
    targetElement: 'stats-button',
    position: 'bottom'
  }
];

const initialState: TutorialState = {
  hasSeenTutorial: false,
  isActive: false,
  currentStepIndex: 0,
  showWelcomePopup: false,
  steps: tutorialSteps
};

const tutorialSlice = createSlice({
  name: 'tutorial',
  initialState,
  reducers: {
    // Initialize tutorial state from localStorage
    initializeTutorial: (state, action: PayloadAction<boolean>) => {
      state.hasSeenTutorial = action.payload;
      state.showWelcomePopup = !action.payload;
    },
    // Start the guided tutorial
    startTutorial: (state) => {
      state.isActive = true;
      state.currentStepIndex = 0;
      state.showWelcomePopup = false;
    },
    // Skip tutorial and mark as seen
    skipTutorial: (state) => {
      state.isActive = false;
      state.showWelcomePopup = false;
      state.hasSeenTutorial = true;
      state.currentStepIndex = 0;
    },
    // Move to next step
    nextStep: (state) => {
      if (state.currentStepIndex < state.steps.length - 1) {
        state.currentStepIndex += 1;
      } else {
        // Tutorial completed
        state.isActive = false;
        state.hasSeenTutorial = true;
        state.currentStepIndex = 0;
      }
    },
    // Move to previous step
    previousStep: (state) => {
      if (state.currentStepIndex > 0) {
        state.currentStepIndex -= 1;
      }
    },
    // Go to specific step
    goToStep: (state, action: PayloadAction<number>) => {
      if (action.payload >= 0 && action.payload < state.steps.length) {
        state.currentStepIndex = action.payload;
      }
    },
    // Exit tutorial
    exitTutorial: (state) => {
      state.isActive = false;
      state.hasSeenTutorial = true;
      state.currentStepIndex = 0;
    },
    // Manually trigger welcome popup (for "What's New" button)
    showWelcome: (state) => {
      state.showWelcomePopup = true;
      state.isActive = false;
      state.currentStepIndex = 0;
    },
    // Close welcome popup without starting tutorial
    closeWelcome: (state) => {
      state.showWelcomePopup = false;
    },
    // Reset tutorial (for testing or user preference)
    resetTutorial: (state) => {
      state.hasSeenTutorial = false;
      state.isActive = false;
      state.currentStepIndex = 0;
      state.showWelcomePopup = true;
    }
  }
});

export const {
  initializeTutorial,
  startTutorial,
  skipTutorial,
  nextStep,
  previousStep,
  goToStep,
  exitTutorial,
  showWelcome,
  closeWelcome,
  resetTutorial
} = tutorialSlice.actions;

export default tutorialSlice.reducer;

/** Step IDs that only exist on the "Ask Chat" view (tools, model selector, etc.) */
export const CHAT_ONLY_STEP_IDS = new Set([
  'model-selector', 'web-search',
  'data-analysis', 'document-translation', 'voice-mode', 'file-upload',
]);

/** Step IDs whose target elements live inside the plus-sign dropdown */
export const PLUS_MENU_STEP_IDS = new Set([
  'file-upload', 'web-search', 'data-analysis', 'document-translation',
]);
